import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaCalendarAlt } from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

const formatDateLong = (ymd) => {
  const d = new Date((ymd || "") + "T00:00:00");
  if (Number.isNaN(d.getTime())) return ymd || "";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
};

const orderIdFromBooking = (booking) =>
  booking?.packageBookingId ||
  booking?.diagnosticBookingId ||
  booking?.bookingId ||
  booking?._id ||
  null;

const LabTestsConfirmedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    lineItems = [],
    patient,
    diagnostic,
    selectedDate,
    selectedTime,
    booking,
    fastingNote,
    amountPaid,
    walletUsed,
  } = location.state || {};

  useEffect(() => {
    if (!selectedDate || !selectedTime) navigate("/lab-tests", { replace: true });
  }, [selectedDate, selectedTime, navigate]);

  useEffect(() => {
    if (!selectedDate || !selectedTime) return;
    const t = setTimeout(() => navigate("/home", { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [selectedDate, selectedTime, navigate]);

  if (!selectedDate || !selectedTime) return null;

  const orderId = orderIdFromBooking(booking);

  const downloadReceipt = () => {
    const lines = [
      "Credenthealth — Lab Booking Receipt",
      orderId ? `Order ID: ${orderId}` : "",
      `Patient: ${patient?.name || ""}`,
      `Lab: ${diagnostic?.name || ""}`,
      `Date: ${formatDateLong(selectedDate)}`,
      `Time: ${selectedTime}`,
      "",
      "Items:",
      ...lineItems.map((i) => `- ${i.title}: ₹${Number(i.price || 0).toLocaleString("en-IN")}`),
      "",
      walletUsed ? `Wallet applied: ₹${Number(walletUsed).toLocaleString("en-IN")}` : "",
      amountPaid != null ? `Total: ₹${Number(amountPaid).toLocaleString("en-IN")}` : "",
    ].filter(Boolean);
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `lab-booking-${orderId || "receipt"}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="bg-background min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="bg-surface-lowest rounded-2xl shadow-md border border-surface-container max-w-2xl w-full p-8">
            <div className="w-14 h-14 rounded-full bg-primary-container mx-auto flex items-center justify-center mb-4">
              <FaCheck className="text-white" size={22} />
            </div>
            <h1 className="text-2xl font-bold text-center text-on-primary-fixed-var">
              Your health check-up is scheduled!
            </h1>
            <p className="text-center text-on-surface-variant mt-2 mb-4">
              We&apos;ve confirmed your lab appointment. Details are below.
            </p>
            <p className="text-center text-xs text-on-surface-variant mb-6">
              Redirecting to dashboard in 3 seconds…
            </p>

            {orderId && (
              <p className="text-center text-sm font-semibold text-primary mb-4">Order ID: {orderId}</p>
            )}

            <div className="bg-primary-fixed/30 border border-surface-container rounded-xl p-4 flex gap-3 mb-4">
              <FaCalendarAlt className="text-primary mt-1 shrink-0" />
              <div>
                <p className="text-xs uppercase text-on-surface-variant">Next steps</p>
                <p className="font-bold">{formatDateLong(selectedDate)}</p>
                <p className="text-sm text-on-surface-variant">{selectedTime}</p>
                {diagnostic?.name && (
                  <p className="text-sm text-on-surface-variant mt-1">At {diagnostic.name}</p>
                )}
                {fastingNote && (
                  <p className="text-sm text-amber-700 mt-2 font-medium">
                    Please fast for 8–12 hours before your test if required.
                  </p>
                )}
              </div>
            </div>

            {lineItems.length > 0 && (
              <ul className="text-sm text-on-surface-variant mb-6 space-y-1">
                {lineItems.map((i) => (
                  <li key={i.id}>• {i.title}</li>
                ))}
              </ul>
            )}

            <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
              <button
                type="button"
                onClick={() => navigate("/home")}
                className="px-5 py-2.5 rounded-lg bg-primary text-white font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                type="button"
                onClick={downloadReceipt}
                className="px-5 py-2.5 rounded-lg border border-primary text-primary font-semibold"
              >
                Download Receipt
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default LabTestsConfirmedPage;
